import { isValid, parseISO } from 'date-fns'
import ProductWithAvatar from './ProductWithAvatar'
import Price from '../Price'
import { formatDateAsDayMontYearHHMM } from '../../../../../utils/date/dateFormat'
import InfoLine from '../../../../../ui/InfoLine/InfoLine'
import ActiveStatusBadge from '../../../../../ui/ActiveStatusBadge/ActiveStatusBadge'
import getOutletReasonLabel from '../../getOutletReasonLabel'

export const PRODUCTS_TABLE_VARIANTS = {
  catalog: 'catalog',
  outlet: 'outlet',
}

const OUTLET_REASON_LABELS = {
  exitBrand: 'Вивід бренду',
  exitProduct: 'Вивід окремих позицій',
  productExpiration: 'Експірація товару',
}

const toDate = (value) => {
  if (value == null || value === '') return null

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const date = value.toDate()
      return isValid(date) ? date : null
    }
    if (value.seconds != null) {
      return new Date(value.seconds * 1000)
    }
    if (value._seconds != null) {
      return new Date(value._seconds * 1000)
    }
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null
  }

  if (typeof value === 'number') {
    const ms = value > 1e12 ? value : value * 1000
    const date = new Date(ms)
    return isValid(date) ? date : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    const uaDate = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
    if (uaDate) {
      const date = new Date(Number(uaDate[3]), Number(uaDate[2]) - 1, Number(uaDate[1]))
      return isValid(date) ? date : null
    }

    const isoDate = parseISO(trimmed)
    if (isValid(isoDate)) return isoDate

    const parsed = new Date(trimmed)
    if (isValid(parsed)) return parsed
  }

  return null
}

const formatCellDateTime = (value) => {
  const date = toDate(value)
  if (!date) return value ? String(value) : '-'
  return formatDateAsDayMontYearHHMM({ seconds: Math.floor(date.getTime() / 1000) })
}

const formatDateOnlyLabel = (value) => {
  if (typeof value === 'string' && value.trim()) return value.trim()
  const date = toDate(value)
  if (!date) return value ? String(value) : '—'
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  return `${dd}.${mm}.${date.getFullYear()}`
}

const startOfLocalDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const isExpirationDateInPast = (date) => {
  if (!date) return false
  return startOfLocalDay(date).getTime() < startOfLocalDay(new Date()).getTime()
}

const getOutletRemainQuantity = (row) => {
  const outletRemain = Array.isArray(row?.remains) ? row.remains[0] : null
  const quantity = Number(outletRemain?.quantity)
  return Number.isFinite(quantity) ? quantity : 0
}

const getSortedOutletExpirationRules = (row) => {
  if (row?.outletReason !== 'productExpiration') return null

  const rules = Array.isArray(row.expirationRules) ? row.expirationRules : []
  if (!rules.length) return null

  const highlightPast =
    row?.isActive !== false && getOutletRemainQuantity(row) > 0

  return [...rules]
    .map((rule, index) => {
      const date = toDate(rule?.expirationDate)
      const quantity = Number(rule?.quantity)

      return {
        key: rule?.id != null ? String(rule.id) : String(index),
        sortKey: date ? date.getTime() : Number.POSITIVE_INFINITY,
        dateLabel: formatDateOnlyLabel(rule?.expirationDate),
        quantity: Number.isFinite(quantity) ? quantity : rule?.quantity ?? '—',
        isPast: highlightPast && isExpirationDateInPast(date),
      }
    })
    .sort((a, b) => a.sortKey - b.sortKey)
}

const COLUMN_DEFS = {
  barcode: {
    field: 'barcode',
    headerClassName: 'TableHeadCol',
    headerName: 'Код товару',
    width: 150,
  },
  isActive: {
    field: 'isActive',
    headerClassName: 'TableHeadCol',
    headerName: 'Активний',
    width: 110,
    renderCell: (params) => <ActiveStatusBadge isActive={params.row.isActive} />,
  },
  category: {
    field: 'category',
    headerClassName: 'TableHeadCol',
    headerName: 'Категорія',
    width: 150,
  },
  subcategory: {
    field: 'subcategory',
    headerClassName: 'TableHeadCol',
    headerName: 'Підкатегорія',
    renderCell: (params) => (params.row.subcategory ? params.row.subcategory.title : '-'),
    width: 150,
  },
  taxonomy: {
    field: 'taxonomySummary',
    headerClassName: 'TableHeadCol',
    headerName: 'Категорії',
    width: 260,
    renderCell: (params) => {
      const lines = params.row.taxonomySummaryLines
      const tooltip = (params.row.taxonomyTooltipLines || []).join('\n')
      if (!lines?.length) return <span title={tooltip || undefined}>—</span>
      if (lines.length === 1) return <span title={tooltip || undefined}>{lines[0]}</span>
      return (
        <div title={tooltip || undefined}>
          {lines.map((line, index) => (
            <div key={`${index}-${line}`}>{line}</div>
          ))}
        </div>
      )
    },
  },
  taxonomyMain: {
    field: 'taxonomyMainLine',
    headerClassName: 'TableHeadCol',
    headerName: 'Основна категорія',
    width: 220,
    renderCell: (params) => {
      const line = params.row.taxonomyMainLine || 'Не вибрано'
      return <span title={line}>{line}</span>
    },
  },
  taxonomyExtras: {
    field: 'taxonomyExtrasPreviewLines',
    headerClassName: 'TableHeadCol',
    headerName: 'Додаткові категорії',
    width: 220,
    sortable: false,
    renderCell: (params) => {
      const lines = params.row.taxonomyExtrasPreviewLines || []
      const tooltip = params.row.taxonomyExtrasTooltip || ''
      if (!lines.length) return <span title={tooltip || undefined}>—</span>
      if (lines.length === 1) return <span title={tooltip || undefined}>{lines[0]}</span>
      return (
        <div title={tooltip || undefined}>
          {lines.map((line, index) => (
            <div key={`${index}-${line}`}>{line}</div>
          ))}
        </div>
      )
    },
  },
  product: {
    field: 'product',
    headerName: 'Товар',
    headerClassName: 'TableHeadCol',
    minWidth: 300,
    flex: 1,
    renderCell: (params) => (
      <ProductWithAvatar
        id={params.row.id}
        avatar={params.row.avatar}
        name={params.row.name}
        description={params.row.descriptionShort}
      />
    ),
  },
  price: {
    field: 'price',
    headerClassName: 'TableHeadCol',
    headerName: 'Ціна',
    renderCell: (params) => <Price num={params.row.price} stroked={params.row.specPrice && true} />,
    width: 100,
  },
  specPriceCatalog: {
    field: 'specPrice',
    headerClassName: 'TableHeadCol',
    headerName: 'Акційна ціна',
    renderCell: (params) => (params.row.specPrice ? <Price num={params.row.specPrice} highlighted /> : '-'),
    width: 100,
  },
  specPriceOutlet: {
    field: 'specPrice',
    headerClassName: 'TableHeadCol',
    headerName: 'Спеціальна ціна',
    renderCell: (params) => (params.row.specPrice ? <Price num={params.row.specPrice} highlighted /> : '-'),
    width: 120,
  },
  specPriceDate: {
    field: 'specPriceDate',
    headerClassName: 'TableHeadCol',
    headerName: 'Термін дії акції',
    renderCell: (params) => (params.row.specPriceDate ? params.row.specPriceDate : '-'),
    width: 120,
  },
  outletReason: {
    field: 'outletReason',
    headerClassName: 'TableHeadCol',
    headerName: 'Причина outlet',
    width: 180,
    renderCell: (params) => getOutletReasonLabel(params.row.outletReason),
  },
  expirationDates: {
    field: 'expirationRules',
    headerClassName: 'TableHeadCol',
    headerName: 'Дата експірації',
    width: 170,
    sortable: false,
    renderCell: (params) => {
      const lines = getSortedOutletExpirationRules(params.row)
      if (!lines) return '-'

      return (
        <div>
          {lines.map((line) => (
            <InfoLine
              key={line.key}
              title={
                line.isPast ? <span style={{ color: 'red' }}>{line.dateLabel}</span> : line.dateLabel
              }
              content={line.quantity}
            />
          ))}
        </div>
      )
    },
  },
  amount: {
    field: 'amount',
    headerClassName: 'TableHeadCol',
    headerName: 'Од. виміру',
    width: 100,
  },
  quantityCatalog: {
    field: 'quantity',
    headerClassName: 'TableHeadCol',
    headerName: 'Залишки',
    width: 150,
    renderCell: (params) => (
      <div>
        {params.row.remains.map((s) => (
          <InfoLine
            key={s.id}
            title={s.name}
            content={s.quantity <= 0 ? <span style={{ color: 'red' }}>{s.quantity}</span> : s.quantity}
          />
        ))}
      </div>
    ),
  },
  quantityOutlet: {
    field: 'quantity',
    headerClassName: 'TableHeadCol',
    headerName: 'Залишки',
    width: 100,
    renderCell: (params) => {
      const outletRemain = params.row.remains[0]

      if (!outletRemain) return '-'

      return outletRemain.quantity <= 0 ? (
        <span style={{ color: 'red' }}>{outletRemain.quantity}</span>
      ) : (
        outletRemain.quantity
      )
    },
  },
  updated: {
    field: 'updated',
    headerClassName: 'TableHeadCol',
    headerName: 'Ост. оновлення',
    renderCell: (params) => formatCellDateTime(params.row.updated),
    width: 200,
  },
  id: {
    field: 'id',
    headerClassName: 'TableHeadCol',
    headerName: 'ID',
    renderCell: (params) => (
      <div>
        <InfoLine title="id" content={params.row.id} />
        <InfoLine title="cashboxId" content={params.row.cashboxId || params.row.cashboxProductId || '-'} />
      </div>
    ),
    width: 250,
  },
}

const VARIANT_COLUMN_KEYS = {
  [PRODUCTS_TABLE_VARIANTS.catalog]: [
    'barcode',
    'isActive',
    'taxonomyMain',
    'taxonomyExtras',
    'product',
    'price',
    'specPriceCatalog',
    'specPriceDate',
    'amount',
    'quantityCatalog',
    'updated',
    'id',
  ],
  [PRODUCTS_TABLE_VARIANTS.outlet]: [
    'barcode',
    'isActive',
    'taxonomyMain',
    'taxonomyExtras',
    'product',
    'price',
    'specPriceOutlet',
    'outletReason',
    'expirationDates',
    'amount',
    'quantityOutlet',
    'updated',
    'id',
  ],
}

export function getProductsTableColumns(variant = PRODUCTS_TABLE_VARIANTS.catalog) {
  const keys = VARIANT_COLUMN_KEYS[variant] || VARIANT_COLUMN_KEYS[PRODUCTS_TABLE_VARIANTS.catalog]
  return keys.map((key) => COLUMN_DEFS[key])
}

export function isOutletTableVariant(variant) {
  return variant === PRODUCTS_TABLE_VARIANTS.outlet
}
